import mitt from 'mitt';
import * as Sentry from '@sentry/vue';

/**
 * Barramento de eventos da aplicação.
 *
 * Substitui o `export const bus = new Vue()` que vivia em `src/main.js`. A troca é
 * necessária porque `$on`/`$off`/`$once` foram removidos da instância no Vue 3, e é
 * possível hoje porque um emitter funciona igual sob Vue 2.7.
 *
 * Mora aqui, e não em `main.js`, para cortar um ciclo de import que funcionava por
 * sorte de ordem de avaliação: os módulos de `src/store/modules/` importavam `@/main`,
 * e `main.js` importa `@/store/store`. De quebra, as specs deixam de carregar
 * `main.js` — e com ele um `new Vue(...).$mount("#app")` — só para obter o barramento.
 *
 * O wrapper existe por um motivo específico, não por estilo. `Vue.prototype.$emit`
 * invoca cada handler dentro de `invokeWithErrorHandling`, que encaminha a exceção para
 * `Vue.config.errorHandler` e, por ele, para o Sentry: um listener que quebra não
 * impede os demais de rodar. O mitt faz `handlers.slice().map(h => h(evt))` sem
 * try/catch, então a primeira exceção aborta os listeners seguintes e sobe pela pilha
 * de quem emitiu. Três eventos deste projeto têm mais de um ouvinte e mudariam de
 * comportamento:
 *
 *   novoCadastroAdicionado  4 ouvintes (caixa-venda, caixa-venda-detalhada,
 *                                       nova-despesa, nova-receita)
 *   clear                   2 (caixa-sangria, caixa-reforco)
 *   clearReceberContaFiado  2 (caixa-recebimento-fiado, -multiplo)
 *
 * Sem o isolamento, um cadastro novo que falhasse em um modal deixaria os outros três
 * sem atualizar — sem erro visível e sem nada no Sentry.
 */
const emitter = mitt();

export const bus = {
  on: (tipo, handler) => emitter.on(tipo, handler),

  /** Sem handler, remove todos os ouvintes do tipo — mesma semântica do `$off` do Vue 2. */
  off: (tipo, handler) => emitter.off(tipo, handler),

  emit(tipo, payload) {
    const handlers = emitter.all.get(tipo);
    if (!handlers || !handlers.length) return;
    // slice() porque um handler pode chamar off() durante o próprio despacho
    for (const handler of handlers.slice()) {
      try {
        handler(payload);
      } catch (erro) {
        Sentry.captureException(erro, {
          tags: { origem: 'event-bus' },
          extra: { evento: tipo },
        });
        // eslint-disable-next-line no-console
        console.error(`[bus] listener de "${tipo}" lançou:`, erro);
      }
    }
  },
};

export default bus;
